import React, {
  useState,
  useEffect,
  memo,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useUnmount } from 'ahooks';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import {
  IDomEditor,
  IEditorConfig,
  i18nChangeLanguage,
  IToolbarConfig,
  DomEditor,
} from '@wangeditor/editor';
import '@wangeditor/editor/dist/css/style.css'; // 引入 css
import classNames from 'classnames';
// @ts-ignore
import './index.scss';

// 切换语言 - 'en' 或者 'zh-CN'
i18nChangeLanguage('en');

interface Props {
  isBackShow?: boolean; // 是否是回显模式
  getEditorContent: ((v: string, isInited: boolean) => void) | null; // 拿到当前富文本内容
  getEditorText?: (v: string, isInited: boolean) => void; // 拿到当前富文本纯文本内容
  getIsFocused?: (v: boolean) => void; // 判断当前编辑器是否聚焦
  status?: 'error' | 'success';
  errorTips?: string;
  defaultValue?: string;
  placeholder?: string;
  autoFocus?: boolean;
  maxLength?: number;
  customStyle?: React.CSSProperties;
  className?: string;
  isShowToolbar?: boolean;
  readOnly?: boolean;
  maxContentHeight?: number; // 传了这个属性内容超出高度将出现滚动条
  [keyName: string]: any;
}

const DEFAULT_PROPS: Props = {
  isBackShow: false,
  getEditorContent: null,
  status: 'success',
  errorTips: '',
  defaultValue: '',
  placeholder: '',
  autoFocus: true,
  maxLength: undefined,
  customStyle: {},
  className: '',
  isShowToolbar: true,
  readOnly: false,
  maxContentHeight: 0,
};

function index(props: Props) {
  const {
    isBackShow,
    getEditorContent,
    getEditorText,
    getIsFocused,
    status,
    errorTips,
    defaultValue,
    placeholder,
    autoFocus,
    maxLength,
    customStyle,
    className,
    isShowToolbar,
    readOnly,
    maxContentHeight,
  } = props;

  const [editor, setEditor] = useState<IDomEditor | null>(null); // 存储 editor 实例
  const [html, setHtml] = useState<string>(''); // 编辑器内容
  const [isInit, setIsInit] = useState<boolean>(true); // 记录是否为初始化
  // 时间防抖
  const [startTime, setStartTime] = useState<number>(0);
  const currentWangEditorRef = useRef<HTMLDivElement>(null);

  // 查看配置信息

  // 工具栏配置
  const toolbarConfig: Partial<IToolbarConfig> = {
    toolbarKeys: [
      // "header1",
      // "header2",
      // "header3",
      // "headerSelect",
      'bold',
      'underline',
      // 'italic',
      'through',
      // "color",
      // "bgColor",
      'bulletedList',
      'numberedList',
      {
        key: 'group-justify-style',
        title: 'justify',
        iconSvg: `<svg viewBox="0 0 1024 1024"><path d="M768 793.6v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z m-204.8-230.4v102.4H51.2v-102.4h716.8zM972.8 102.4v102.4H51.2V102.4h921.6z"></path></svg>`,
        menuKeys: [
          'justifyLeft',
          'justifyRight',
          'justifyCenter',
          'justifyJustify',
        ],
      },
      'clearStyle',
      'undo',
      'redo',
    ],
    excludeKeys: [],
  };

  const onFocus = useCallback((editor: IDomEditor) => {
    getIsFocused?.(editor?.isFocused());
  }, []);
  const onBlur = useCallback((editor: IDomEditor) => {
    getIsFocused?.(editor?.isFocused());
  }, []);

  // 编辑器配置
  const editorConfig: Partial<IEditorConfig> = {
    placeholder,
    autoFocus,
    scroll: true,
    maxLength,
    onFocus,
    onBlur,
    customPaste: (editor: IDomEditor, event: ClipboardEvent): boolean => {
      const html = event?.clipboardData?.getData('text/html');
      // 有&nbsp;的转义符时替换成正常的空格，这样单词才能正常换行，页面dom没有&nbsp;
      const flag = html?.includes('&nbsp;');
      if (flag) {
        let newHtml: string = html?.replaceAll('&nbsp;', ' ') || '';
        editor?.dangerouslyInsertHtml(newHtml);
        // 阻止默认的粘贴行为
        event?.preventDefault();
        return false;
        /* const startIndex = newHtml?.lastIndexOf('<!--StartFragment-->') || -1;
        const endIndex = newHtml?.indexOf('<!--EndFragment-->') || -1;
        if (startIndex !== -1 && endIndex !== -1) {
          editor?.insertText(newHtml?.slice(startIndex + 20, endIndex));
          // 阻止默认的粘贴行为
          event?.preventDefault();
          return false;
        } else {
          return true;
        } */
      } else {
        return true;
      }
    },
  };

  // 默认数据展示
  useEffect(() => {
    if (defaultValue == html || new Date().getTime() - startTime < 500) return;
    if (
      defaultValue &&
      defaultValue?.slice(0, 1) === '<' &&
      defaultValue?.slice(-1) === '>'
    ) {
      setHtml(defaultValue || '');
    } else {
      setHtml(`<p>${defaultValue}</p>` || '');
    }
    setStartTime(new Date().getTime());
    setIsInit(true);
  }, [defaultValue]);

  useEffect(() => {
    if (!editor) return;
    const isEmpty = html === '<p><br></p>' || editor?.isEmpty() || !html;
    if (isInit && !isEmpty) {
      setIsInit(false);
      getEditorContent?.(isEmpty ? '' : html, false);
      getEditorText?.(editor?.getText() || '', false);
    }
    if (isInit) return;
    getEditorContent?.(isEmpty ? '' : html, true);
    getEditorText?.(editor?.getText() || '', true);
  }, [html, editor]);

  // 及时销毁 editor ，重要！
  // useEffect(() => {
  //   return () => {
  //     if (editor == null) return;
  //     editor.destroy();
  //     setEditor(null);
  //   };
  // }, [editor]);

  // 及时销毁 editor ，重要！
  useUnmount(() => {
    if (editor === null) return;
    editor?.blur();
    setTimeout(() => {
      editor?.destroy();
      setEditor(null);
    }, 1000);
  });

  // 切换编辑器是否只读
  useEffect(() => {
    if (editor === null) return;
    if (readOnly || isBackShow) {
      editor.disable();
    } else {
      editor.enable();
    }
  }, [editor, readOnly, isBackShow]);

  // 设置内容滚动
  useEffect(() => {
    if (!maxContentHeight || !currentWangEditorRef.current) return;
    // @ts-ignore
    const WEScrollContent: HTMLDivElement[] =
      currentWangEditorRef?.current?.getElementsByClassName('w-e-scroll');
    if (WEScrollContent) {
      WEScrollContent[0].style.maxHeight = `${maxContentHeight}px`;
    }
  }, [maxContentHeight]);

  /* const beforePasteHandle = useCallback((e) => {
    let pasteCon = e?.clipboardData.getData('text/html') || navigator?.clipboard?.readText();
    pasteCon = pasteCon?.replaceAll(' ', '&nbsp;');
    const focusEl: any = document?.activeElement // 获取当前焦点所在元素
    if (!focusEl || focusEl?.tagName?.toUpperCase() === 'BODY') {return false;}
    focusEl.value = pasteCon;
  }, [editor]);

  // 粘贴前把粘贴内容的&nbsp;换成普通空格
  useEffect(() => {
    window.addEventListener('paste', beforePasteHandle);

    return () => {
      window.removeEventListener('paste', beforePasteHandle);
    };
  }, []); */

  return (
    <>
      <div
        className={classNames({
          'dbg-editor-content': true,
          'dbg-wang-edit-content': true,
          'dbg-wang-edit-showing': isBackShow,
          'dbg-error-status': status === 'error',
          [className as string]: !!className,
        })}
        style={customStyle}
        ref={currentWangEditorRef}
      >
        {!!isShowToolbar && (
          <Toolbar
            editor={editor}
            defaultConfig={toolbarConfig}
            mode="simple"
            className='dbg-editor-content-toolbar'
            style={{
              // borderBottom: '1px solid #ddd',
              display: isBackShow ? 'none' : '',
            }}
          />
        )}
        <Editor
          defaultConfig={editorConfig}
          value={html}
          onCreated={setEditor}
          onChange={(editor: any) => {
            const html = editor?.getHtml();
            setHtml(html);
          }}
          mode="simple"
        />
      </div>
      <label
        className={classNames({
          'dbg-error-info': status === 'error',
        })}
        style={{ display: 'none' }}
      >
        {errorTips ||
          'Please check that the information is complete or correct.'}
      </label>
    </>
  );
}

index.defaultProps = DEFAULT_PROPS;

export default memo(index);
